import * as THREE from 'three';
import { markPerformance } from '../../performance-mark.js';
import { compileObjects, type Drawable } from '../scene-warmup.js';
import { preparationFrame } from '../idle-preparation.js';

/** Line width in CSS pixels, the same at every zoom. */
const OUTLINE_PIXELS = 2;
/** A layer nothing else uses: during the cue the camera sees only the figure meshes that carry it. */
export const OUTLINE_LAYER = 30;

async function waitForShaders(compiling: Promise<unknown>) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([compiling, new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error('Outline shader preparation timed out')), 30_000);
    })]);
  } finally { clearTimeout(timer); }
}

/** Solid, depth-writing surfaces get a hull; glass, decals, alpha-cut cards and effects would outline their own quads. */
function outlined(material: THREE.Material) {
  return material.visible && !material.transparent && material.depthWrite && !material.alphaTest && !material.alphaHash
    && material.blending === THREE.NormalBlending && !(material as THREE.MeshBasicMaterial).wireframe;
}
export function hullsMesh(object: THREE.Object3D): object is THREE.Mesh {
  if (!(object instanceof THREE.Mesh) || !object.geometry.getAttribute('normal')) return false;
  return Array.isArray(object.material) ? object.material.some(outlined) : outlined(object.material);
}

/**
 * The hovered, selected or menu-focused figure drawn once more, back faces only, pushed out along their normals by a
 * fixed number of screen pixels in one flat colour (an inverted hull). The world's depth hides it wherever something
 * stands in front. The figure is first stamped into the stencil buffer unextruded, so only the outer rim shows, not the
 * hull of an arm over the chest or of a quiver over the back. No render targets or full-screen passes.
 */
export class UnitHoverOutline {
  readonly color = { value: new THREE.Color(0xff3535) };
  readonly width = { value: OUTLINE_PIXELS };
  readonly resolution = { value: new THREE.Vector2(1, 1) };
  readonly material = new THREE.MeshNormalMaterial({ side: THREE.BackSide, stencilWrite: true, stencilRef: 1, stencilFunc: THREE.NotEqualStencilFunc });
  // The same program with no push: marks the figure's silhouette (even where something covers it) and draws nothing.
  private readonly mask = new THREE.MeshNormalMaterial({ side: THREE.BackSide, colorWrite: false, depthTest: false, depthWrite: false,
    stencilWrite: true, stencilRef: 1, stencilFunc: THREE.AlwaysStencilFunc, stencilZPass: THREE.ReplaceStencilOp });
  // Holds the figures for one draw without reparenting them; the world render already refreshed their matrices.
  private hullScene = Object.assign(new THREE.Scene(), { matrixWorldAutoUpdate: false });
  private warmMeshes: THREE.Mesh[] = [];
  private meshes: THREE.Mesh[] = [];
  private materials: (THREE.Material | THREE.Material[])[] = [];
  private layers: number[] = [];
  constructor(private renderer: THREE.WebGLRenderer, private scene: THREE.Scene, private camera: THREE.Camera) {
    this.hull(this.material, this.width); this.hull(this.mask, { value: 0 });
  }
  /** The renderer needs a stencil buffer (`stencil: true`). */
  private hull(material: THREE.MeshNormalMaterial, width: { value: number }) {
    material.name = 'hover-outline'; material.toneMapped = false;
    material.customProgramCacheKey = () => 'hover-outline';
    material.onBeforeCompile = shader => {
      Object.assign(shader.uniforms, { outlineColor: this.color, outlineWidth: width, outlineResolution: this.resolution });
      // BackSide flips `transformedNormal` inward; undo it, then push the projected vertex out along the normal's screen direction.
      shader.vertexShader = shader.vertexShader.replace('void main() {', 'uniform float outlineWidth;\nuniform vec2 outlineResolution;\nvoid main() {')
        .replace('#include <project_vertex>', `#include <project_vertex>
  vec3 outward = transformedNormal;
  #ifdef FLIP_SIDED
    outward = -outward;
  #endif
  vec2 screen = (projectionMatrix * vec4(outward, 0.)).xy * outlineResolution;
  if (dot(screen, screen) > 1e-12) gl_Position.xy += normalize(screen) / outlineResolution * 2. * outlineWidth * gl_Position.w;`);
      shader.fragmentShader = `uniform vec3 outlineColor;
#include <common>
#include <logdepthbuf_pars_fragment>
#include <clipping_planes_pars_fragment>
void main() {
  #include <clipping_planes_fragment>
  #include <logdepthbuf_fragment>
  gl_FragColor = vec4(outlineColor, 1.);
  #include <colorspace_fragment>
}`;
    };
  }

  resize(width: number, height: number) {
    const ratio = this.renderer.getPixelRatio();
    this.resolution.value.set(width * ratio, height * ratio); this.width.value = OUTLINE_PIXELS * ratio;
  }

  /** One program per vertex path figures use (rigid, skinned, morphed, instanced), compiled inside the loading gate. */
  async prepare(objects: readonly Drawable[], active = () => true, frame = preparationFrame) {
    const representatives = new Map<string, THREE.Mesh>();
    for (const object of objects) if (hullsMesh(object)) {
      const geometry = object.geometry;
      representatives.set(JSON.stringify([object instanceof THREE.SkinnedMesh, object instanceof THREE.InstancedMesh, object instanceof THREE.InstancedMesh && !!object.morphTexture,
        Object.entries(geometry.morphAttributes).map(([name, attributes]) => [name, attributes?.length]), geometry.morphTargetsRelative]), object);
    }
    this.warmMeshes = [...representatives.values()];
    const target = this.renderer.getRenderTarget();
    try {
      // The cue draws onto the canvas, so compile for the canvas's output colour space.
      this.renderer.setRenderTarget(null);
      for (const object of this.warmMeshes) {
        await frame();
        if (!active()) return;
        const original = object.material;
        let compiling: Promise<unknown>;
        try { object.material = this.material; compiling = compileObjects(this.renderer, this.hullScene, this.camera, [object]); }
        finally { object.material = original; }
        await waitForShaders(compiling);
      }
      markPerformance('nimblez:outline-prepared');
    } finally { this.renderer.setRenderTarget(target); }
  }

  /** `afterWorld` runs between the world and its cue, where the picture filters draw the world onto the canvas. */
  render(figures: readonly THREE.Object3D[] = [], color = 0xff3535, afterWorld?: () => void) {
    this.renderer.render(this.scene, this.camera);
    afterWorld?.();
    if (figures.length) this.drawCue(figures, color);
  }

  /** Draw each prepared program once, so its first-use uniforms are set before the first hover. */
  async warm(active = () => true, frame = preparationFrame) {
    for (const mesh of this.warmMeshes) {
      await frame();
      if (!active()) return;
      const visible = mesh.visible;
      try { mesh.visible = true; this.drawCue([mesh], 0xff3535); }
      finally { mesh.visible = visible; }
    }
  }

  private drawCue(figures: readonly THREE.Object3D[], color: number) {
    const { renderer, camera, meshes, materials, layers } = this, shadow = renderer.shadowMap;
    meshes.length = 0;
    for (const figure of figures) figure.traverseVisible(object => { if (hullsMesh(object)) meshes.push(object); });
    if (!meshes.length) return;
    this.color.value.setHex(color);
    const autoClear = renderer.autoClear, autoUpdate = shadow.autoUpdate, needsUpdate = shadow.needsUpdate, cameraLayers = camera.layers.mask;
    for (let i = 0; i < meshes.length; i++) { materials[i] = meshes[i].material; layers[i] = meshes[i].layers.mask; }
    const children = this.hullScene.children;
    try {
      for (const mesh of meshes) mesh.layers.enable(OUTLINE_LAYER);
      camera.layers.set(OUTLINE_LAYER);
      renderer.autoClear = false; shadow.autoUpdate = false; shadow.needsUpdate = false;
      this.hullScene.children = figures as THREE.Object3D[];
      renderer.clear(false, false, true);
      for (const hull of [this.mask, this.material]) {
        for (let i = 0; i < meshes.length; i++) {
          const original = materials[i];
          meshes[i].material = Array.isArray(original) ? original.map(material => outlined(material) ? hull : material) : hull;
        }
        renderer.render(this.hullScene, camera);
      }
    } finally {
      this.hullScene.children = children;
      renderer.autoClear = autoClear; shadow.autoUpdate = autoUpdate; shadow.needsUpdate = needsUpdate; camera.layers.mask = cameraLayers;
      for (let i = 0; i < meshes.length; i++) { meshes[i].material = materials[i]; meshes[i].layers.mask = layers[i]; }
      materials.length = 0;
    }
  }
  dispose() { this.material.dispose(); this.mask.dispose(); }
}
